<template>
  <div v-sight-photos-view class="photos-preview">
    <PhotosGrid v-if="!isMobile" :photos="displayedPhotos" :layout="layout" @photo-click="onPhotoClick" />
    <MobileScroll v-else :photos="photos" @photo-click="onPhotoClick" />

    <GalleryTrigger
      v-if="props.experience?.id"
      ref="galleryTriggerRef"
      :gallery-origin="galleryOrigin"
      :gallery-photos-list="galleryPhotosList"
      :more-photos-count="morePhotoCount"
      :preview-type="GalleryPreviewType.EXPERIENCE_SLIDER_GALLERY"
      :title="expTitle"
      :trigger-max-photos-count="maxPhotosCount"
      :sogu-url="soguUrl"
      @on-trigger-click="decreaseZIndex"
      @on-close-gallery-slider="increaseZIndex"
      @on-close-gallery-list="increaseZIndex"
    />

    <GalleryButton v-if="morePhotoCount" :count="morePhotoCount" @click="onGalleryButtonClick" />
  </div>
</template>

<script setup lang="ts">
import {
  GalleryOriginType,
  GalleryPreviewType,
  GalleryTrigger,
  type IGalleryTriggerProps,
} from '@experience/gallery-slider';
import { useI18n } from '@experience/i18n';
import { computed, ref } from 'vue';

import { getRuntimeConfig } from '@/shared/config/runtime';
import { useAnalytics } from '@/shared/lib/analytics';
import { useHeaderZIndex } from '@/shared/lib/headerZIndex';
import { useScreenSize } from '@/shared/utils/breakpoints';

import GalleryButton from './GalleryButton.vue';
import MobileScroll from './MobileScroll.vue';
import PhotosGrid from './PhotosGrid.vue';
import ru from '../i18n/ru.json';
import { getDisplayedPhotos, getLayoutSlice } from '../lib/get-displayed-photos';
import { getPreviewLayout } from '../lib/get-preview-layout';

import type { ExperiencePhoto, PhotosPreviewExperience } from '../types/experience';

type Props = {
  photos: ExperiencePhoto[];
  experience?: PhotosPreviewExperience;
  experienceId: number;
};

type GalleryTriggerExpose = InstanceType<typeof GalleryTrigger> & {
  onTriggerClick: (index: number) => Promise<void>;
};

type GallerySlide = IGalleryTriggerProps['galleryPhotosList'][number];

const props = defineProps<Props>();
const { isMobile } = useScreenSize();
const { trackEvent, useViewTracker } = useAnalytics();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const galleryTriggerRef = ref<GalleryTriggerExpose | null>(null);
const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();

const soguUrl = getRuntimeConfig().VITE_RUNTIME_SOGU_SERVICE_URL;

const expTitle = computed(() => props.experience?.title || '');

const layout = computed(() => {
  if (!props.photos.length) {
    return 'horizontal-horizontal';
  }
  return getPreviewLayout(props.photos);
});

const displayedPhotos = computed(() => {
  const countOfSlice = getLayoutSlice(layout.value);
  if (!props.photos.length) {
    return Array.from({ length: countOfSlice }, () => ({}) as ExperiencePhoto);
  }
  return getDisplayedPhotos(props.photos, layout.value).slice(0, countOfSlice);
});

const galleryPhotosList = computed((): GallerySlide[] =>
  props.photos.map((photo, index) => ({
    id: `photos-preview-${props.experience?.id ?? 0}-${index}`,
    index,
    originId: props.experience?.id || 0,
    originType: GalleryOriginType.EXP_PLAN,
    srcPhoto: photo.medium || photo.thumbnail || '',
    thumbPhoto: photo.thumbnail || photo.medium || '',
    alt: t('photoAlt', { index: index + 1 }),
  })),
);

const morePhotoCount = computed(() => props.photos.length);

const vSightPhotosView = useViewTracker('sight:photos.view');

const galleryOrigin = computed(() => ({
  id: props.experience?.id || 0,
  type: GalleryOriginType.EXPERIENCE,
  title: expTitle.value,
  description: '',
  number: 1,
}));

const maxPhotosCount = computed(() => 3);

const getPhotoId = (index: number) => props.photos[index]?.id ?? index;

const handleTriggerClick = (index: number) => trackEvent('sight:photos.click', { photo_id: getPhotoId(index) });

const onPhotoClick = async (index: number) => {
  void handleTriggerClick(index);
  decreaseZIndex();
  await galleryTriggerRef.value?.onTriggerClick(index);
};

const onGalleryButtonClick = () => {
  void handleTriggerClick(0);
  decreaseZIndex();
  void galleryTriggerRef.value?.onTriggerClick(0);
};
</script>

<style scoped lang="scss">
.photos-preview {
  position: relative;
  isolation: isolate;
}

:deep(.gallery-trigger) {
  display: none;
}
</style>
